/* eslint-disable max-lines-per-function */
import { toast } from '@/hooks/use-toast';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@e2n/ui/v2';
import { Bewertung } from '@/domain/mitarbeiter/features/rotationsplan/entities/Bewertung';
import { useDeleteBewertung } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useDeleteBewertung/useDeleteBewertung';

export function DeleteBewertung({ bewertung }: { bewertung: Bewertung }) {
  const { id } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [deleteBewertung] = useDeleteBewertung();
  const { t } = useTranslation();
  const navigate = useNavigate();

  async function handleDelete() {
    try {
      setIsSubmitting(true);
      const result = await deleteBewertung({
        id: bewertung.id,
      });

      if (
        result.error ||
        result.data?.mitarbeiter_deleteRotationsplanBewertung.error
      ) {
        toast({
          variant: 'destructive',
          description: t(
            'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteBewertung.error',
          ),
        });
      } else {
        navigate(`/mitarbeiter/rotationsplan/${id}`);

        toast({
          description: t(
            'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteBewertung.success',
          ),
        });
      }
    } catch (_err) {
      toast({
        variant: 'destructive',
        description: t(
          'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteBewertung.error',
        ),
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="hidden md:flex">
          <DialogHeader>
            <DialogTitle>
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteBewertung.title',
              )}
            </DialogTitle>
            <DialogDescription>
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteBewertung.subtitle',
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter variant="default">
            <Button
              onClick={() => {
                setIsDialogOpen(false);
              }}
              variant="secondary-blue">
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteBewertung.dismiss',
              )}
            </Button>
            <Button
              disabled={isSubmitting}
              variant="primary-pill"
              onClick={handleDelete}>
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteBewertung.confirm',
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="hidden md:flex">
        <Button
          disabled={isSubmitting}
          onClick={() => setIsDialogOpen(true)}
          variant="secondary-red-on-grey"
          iconOnly>
          <FontAwesomeIcon icon={faTrashCan} />
        </Button>
      </div>

      <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <DrawerTrigger className="flex md:hidden" asChild>
          <Button
            onClick={() => setIsDrawerOpen(true)}
            disabled={isSubmitting}
            variant="secondary-red-on-grey"
            iconOnly>
            <FontAwesomeIcon icon={faTrashCan} />
          </Button>
        </DrawerTrigger>
        <DrawerContent className="md:hidden">
          <DrawerHeader>
            <DrawerTitle>
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteBewertung.title',
              )}
            </DrawerTitle>
          </DrawerHeader>
          <DrawerBody>
            <DrawerDescription>
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteBewertung.subtitle',
              )}
            </DrawerDescription>
            <DrawerFooter>
              <Button
                variant="primary-pill"
                disabled={isSubmitting}
                onClick={handleDelete}>
                {t(
                  'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteBewertung.confirm',
                )}
              </Button>
              <Button
                onClick={() => {
                  setIsDrawerOpen(false);
                }}
                variant="secondary-blue">
                {t(
                  'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteBewertung.dismiss',
                )}
              </Button>
            </DrawerFooter>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}
